/**
 * The newsletter WORKSPACE: the shared shell all three newsletter screens live inside.
 * It loads the newsletter and its sections exactly once, holds them as the single
 * source of truth, renders a COMPACT workspace header (the platform top bar already
 * carries the newsletter name/switcher, so this header keeps only a status + address +
 * cadence context line and the section nav), and hands the loaded data to whichever
 * child route is active through the router Outlet. The section nav has three sections -
 * STUDIO (the editor), AUDIENCE (subscribers + sending), BASICS (setup + schedule +
 * delete) - each a child route under this layout, so switching sections never
 * refetches the newsletter.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react';
import { Link, NavLink, Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { type Cadence, type Newsletter } from '@nuff/types';
import { Badge, Button, Notification } from '@nuff/ui/react';

import {
  discoverSources,
  getIssue,
  getMe,
  listSections,
  type ApiDeps,
  type SuggestedSection,
} from '../../api';
import { useAuth } from '../../auth/context';
import { issueSectionSignature } from './IssueRenderer';
import {
  EMPTY_ISSUE_CACHE,
  EMPTY_SUBSCRIBERS_CACHE,
  type ChatTurn,
  type DiscoverState,
  type IssueCache,
  type SubscribersCache,
} from './panels';

type LoadState = 'loading' | 'ready' | 'notfound' | 'error';

/** Friendly, jargon-free cadence labels for the header meta line. */
const CADENCE_LABELS: Record<Cadence, string> = {
  weekly: 'Weekly',
  biweekly: 'Every two weeks',
  monthly: 'Monthly',
};

/**
 * The workspace data + callbacks handed to the Studio, Audience, and Basics child
 * routes through the Outlet context. Every screen reads the same loaded newsletter and
 * lifted sections, and mutates them through the shared setters so an edit on one
 * screen (a refine turn, a config save) is reflected when the owner switches sections.
 */
export interface NewsletterWorkspaceContext {
  readonly deps: ApiDeps;
  readonly newsletterId: string;
  readonly newsletter: Newsletter;
  readonly setNewsletter: (newsletter: Newsletter) => void;
  readonly sections: readonly SuggestedSection[];
  readonly setSections: (sections: readonly SuggestedSection[]) => void;
  readonly discoverState: DiscoverState;
  readonly discoverError: string | null;
  readonly runDiscover: (withTopics: readonly string[]) => void;
  // ---- Draft/session state hoisted here so it survives a child screen unmounting
  // on a Studio/Audience/Basics tab switch. The persisted draft issue cache is
  // hoisted so a billed GET/generate never re-fires on a tab return, and so a direct
  // Audience load has the draft available to the Send panel without visiting the
  // Studio (the workspace loads it once; see the GET /issue effect below).
  readonly issueCache: IssueCache;
  readonly setIssueCache: Dispatch<SetStateAction<IssueCache>>;
  readonly refineTurns: readonly ChatTurn[];
  readonly setRefineTurns: Dispatch<SetStateAction<readonly ChatTurn[]>>;
  readonly refineInput: string;
  readonly setRefineInput: Dispatch<SetStateAction<string>>;
  readonly refineNextId: MutableRefObject<number>;
  readonly subscribersCache: SubscribersCache;
  readonly setSubscribersCache: Dispatch<SetStateAction<SubscribersCache>>;
  readonly subscriberEmail: string;
  readonly setSubscriberEmail: Dispatch<SetStateAction<string>>;
}

/** Read the workspace context from a Studio/Audience/Basics child route. */
export function useNewsletterWorkspace(): NewsletterWorkspaceContext {
  return useOutletContext<NewsletterWorkspaceContext>();
}

export function NewsletterWorkspace(): React.JSX.Element {
  const { newsletterId = '' } = useParams();
  const { getIdToken, signOut } = useAuth();
  const navigate = useNavigate();

  // Keep the auth + navigation handles in refs so `deps` can stay referentially
  // STABLE for the whole workspace session. react-router recreates `navigate` on
  // every navigation, and a churning `deps` would otherwise re-run the load and
  // section effects each time the owner toggles between Design and Operations,
  // double-fetching the newsletter and clobbering in-flight edits.
  const getIdTokenRef = useRef(getIdToken);
  getIdTokenRef.current = getIdToken;
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const deps = useMemo<ApiDeps>(
    () => ({
      getIdToken: () => getIdTokenRef.current(),
      onUnauthorized: () => {
        signOutRef.current();
        void navigateRef.current('/signin');
      },
    }),
    [],
  );

  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [newsletter, setNewsletter] = useState<Newsletter | null>(null);

  // Sections are lifted here so the sources list, the preview hero, and the
  // schedule gate all read one source of truth across both child screens.
  const [sections, setSections] = useState<readonly SuggestedSection[]>([]);
  const [discoverState, setDiscoverState] = useState<DiscoverState>('idle');
  const [discoverError, setDiscoverError] = useState<string | null>(null);

  // Hoisted draft/session state (see NewsletterWorkspaceContext) so the Studio,
  // Audience, and Basics child screens can unmount on a tab switch without losing it.
  const [issueCache, setIssueCache] = useState<IssueCache>(EMPTY_ISSUE_CACHE);
  const [refineTurns, setRefineTurns] = useState<readonly ChatTurn[]>([]);
  const [refineInput, setRefineInput] = useState('');
  const refineNextId = useRef(0);
  const [subscribersCache, setSubscribersCache] =
    useState<SubscribersCache>(EMPTY_SUBSCRIBERS_CACHE);
  const [subscriberEmail, setSubscriberEmail] = useState('');

  const mounted = useRef(true);
  // One-time auto-discovery latch, reset when the topics actually change so a new
  // set of topics re-discovers.
  const autoTriggered = useRef(false);
  const lastTopicsKey = useRef<string | null>(null);
  // One-time latch for the hoisted GET /issue (see the issue-load effect): a
  // synchronous guard so a section-list change while the GET is in flight cannot fire
  // a second one. Resets per newsletter because the workspace is remounted per id.
  const issueLoadStarted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // No per-instance cache reset is needed here: the workspace is REMOUNTED per
  // newsletter (App keys it by :newsletterId), so switching newsletters mints a
  // fresh instance with fresh caches and a fresh Studio, and an in-flight write for
  // the previous newsletter can never land in the next one's cache (a defence the
  // Studio also enforces with its epoch + newsletterId guards). See C1.

  const topics = newsletter?.topics ?? [];
  const topicsKey = topics.join(' ');

  const load = useCallback(async (): Promise<void> => {
    setLoadState('loading');
    setLoadError(null);
    try {
      const me = await getMe(deps);
      const found = me.newsletters.find((n) => n.newsletterId === newsletterId) ?? null;
      if (!mounted.current) return;
      if (found === null) {
        setLoadState('notfound');
        return;
      }
      setNewsletter(found);
      setLoadState('ready');
    } catch (caught) {
      if (!mounted.current) return;
      setLoadError(caught instanceof Error ? caught.message : 'Could not load this newsletter.');
      setLoadState('error');
    }
  }, [deps, newsletterId]);

  useEffect(() => {
    void load();
  }, [load]);

  const runDiscover = useCallback(
    (withTopics: readonly string[]): void => {
      setDiscoverState('discovering');
      setDiscoverError(null);
      discoverSources(deps, newsletterId, withTopics)
        .then((result) => {
          if (!mounted.current) return;
          setSections(result.sections);
          setDiscoverState('ready');
        })
        .catch((caught: unknown) => {
          if (!mounted.current) return;
          setDiscoverError(
            caught instanceof Error ? caught.message : 'Could not discover sources.',
          );
          setDiscoverState('error');
        });
    },
    [deps, newsletterId],
  );

  // Load persisted sections once the newsletter is ready, and auto-discover once
  // when there are topics but nothing persisted. The auto-discover latch is reset
  // whenever the topic set changes so new topics re-discover.
  useEffect(() => {
    if (loadState !== 'ready' || newsletter === null) {
      return;
    }
    if (lastTopicsKey.current !== topicsKey) {
      lastTopicsKey.current = topicsKey;
      autoTriggered.current = false;
    }
    let active = true;
    setDiscoverState('loading');
    listSections(deps, newsletterId)
      .then((result) => {
        if (!active || !mounted.current) return;
        setSections(result.sections);
        if (result.sections.length > 0) {
          setDiscoverState('ready');
          return;
        }
        if (topics.length > 0 && !autoTriggered.current) {
          autoTriggered.current = true;
          runDiscover(topics);
        } else {
          setDiscoverState('idle');
        }
      })
      .catch(() => {
        if (!active || !mounted.current) return;
        setDiscoverState('idle');
      });
    return () => {
      active = false;
    };
    // `topics` is read through `topicsKey`, which changes exactly when the topic
    // set does, so it is the dependency here rather than the array identity. This
    // deliberately does NOT re-run when the newsletter object changes for other
    // reasons (e.g. a config save that leaves topics untouched), so a save never
    // clobbers the sections the owner has been curating.
  }, [deps, newsletterId, loadState, topicsKey, runDiscover]);

  // C2: load the persisted draft issue ONCE per workspace session, HERE in the shared
  // shell, so a direct Audience (or Basics) deep-link - which never mounts the Studio -
  // still has the draft available for the Send panel. The Studio's own loader is guarded
  // on the same hoisted `loaded` flag, so a normal Studio visit is not double-loaded (and
  // even a rare overlap is harmless: GET /issue reads are idempotent and the C3 marker
  // guard prevents a second generation being enqueued). Mirrors the Studio's settle: a
  // finished issue stamps a built-for-key from its own section signature (so the Studio
  // never needlessly re-bills), while a `generating`/`error` status is surfaced as-is (the
  // Studio polls a generating one when opened). Gated exactly like the Studio loader so it
  // never fires before sections are known or when there is nothing to assemble.
  useEffect(() => {
    if (loadState !== 'ready' || newsletter === null) return;
    if (issueCache.loaded || issueLoadStarted.current) return;
    const sectionsLoading = discoverState === 'loading' || discoverState === 'discovering';
    const hasSelected = sections.some((s) => s.status === 'selected');
    const canAssemble = hasSelected || sections.length > 0 || newsletter.topics.length > 0;
    if (sectionsLoading || !canAssemble) return;

    issueLoadStarted.current = true;
    let active = true;
    setIssueCache((prev) => ({ ...prev, state: 'loading', error: null }));
    getIssue(deps, newsletterId)
      .then((fresh) => {
        if (!active || !mounted.current) return;
        if (fresh.status === 'generating') {
          setIssueCache((prev) => ({ ...prev, state: 'generating', error: null, loaded: true }));
          return;
        }
        if (fresh.status === 'error') {
          setIssueCache((prev) => ({
            ...prev,
            state: 'error',
            error:
              fresh.statusReason ??
              "Our AI is paused for this month because we reached this month's usage limit. It resumes next month.",
            loaded: true,
          }));
          return;
        }
        setIssueCache({
          state: 'ready',
          issue: fresh,
          error: null,
          loaded: true,
          builtForKey: issueSectionSignature(fresh),
        });
      })
      .catch((caught: unknown) => {
        if (!active || !mounted.current) return;
        // Latch `loaded` on error (mirroring the Studio loader) so a persistently
        // failing GET never spins; the error surfaces in the Studio's issue pane.
        setIssueCache((prev) => ({
          ...prev,
          state: 'error',
          error: caught instanceof Error ? caught.message : 'Could not load your issue.',
          loaded: true,
        }));
      });
    return () => {
      active = false;
    };
  }, [
    loadState,
    newsletter,
    issueCache.loaded,
    discoverState,
    sections,
    deps,
    newsletterId,
    setIssueCache,
  ]);

  if (loadState === 'loading') {
    return (
      <section className="dash" aria-labelledby="nl-title">
        <h1 id="nl-title" className="dash__title">
          Newsletter
        </h1>
        <p className="dash__status" role="status">
          Loading this newsletter…
        </p>
      </section>
    );
  }

  if (loadState === 'notfound') {
    return (
      <section className="dash" aria-labelledby="nl-title">
        <h1 id="nl-title" className="dash__title">
          Newsletter not found
        </h1>
        <p className="dash__empty">We could not find that newsletter. It may have been deleted.</p>
        <Link className="nlv__inline-link" to="/app">
          Back to all newsletters
        </Link>
      </section>
    );
  }

  if (loadState === 'error' || newsletter === null) {
    return (
      <section className="dash" aria-labelledby="nl-title">
        <h1 id="nl-title" className="dash__title">
          Newsletter
        </h1>
        <Notification variant="error">{loadError}</Notification>
        <Button className="app__btn-start" onClick={() => void load()}>
          Try again
        </Button>
      </section>
    );
  }

  const context: NewsletterWorkspaceContext = {
    deps,
    newsletterId: newsletter.newsletterId,
    newsletter,
    setNewsletter,
    sections,
    setSections,
    discoverState,
    discoverError,
    runDiscover,
    issueCache,
    setIssueCache,
    refineTurns,
    setRefineTurns,
    refineInput,
    setRefineInput,
    refineNextId,
    subscribersCache,
    setSubscribersCache,
    subscriberEmail,
    setSubscriberEmail,
  };

  return (
    <section className="dash nlv" aria-labelledby="nl-title">
      {/* A COMPACT header: the platform top bar already carries the newsletter name +
          switcher, so this keeps only a status/address/cadence context line and the
          section nav. The name lives on as a VISUALLY-HIDDEN h1 so the section still has
          an accessible landmark heading (screen readers, the aria-labelledby) without
          visibly duplicating the name the top bar already shows. */}
      <header className="nlv__header">
        <h1 id="nl-title" className="nlv__u-visually-hidden">
          {newsletter.name}
        </h1>
        <p className="dash__detail-meta">
          <Badge status={newsletter.status} />
          <span className="dash__item-meta">
            {newsletter.subdomain !== null
              ? `${newsletter.subdomain}.nuffsaid.cloud`
              : 'No address yet'}
          </span>
          <span className="dash__item-meta">
            {newsletter.cadence !== null ? CADENCE_LABELS[newsletter.cadence] : 'Cadence not set'}
          </span>
        </p>
        <nav className="nlv__nav" aria-label="Newsletter sections">
          <NavLink
            to="."
            end
            className={({ isActive }) => `nlv__nav-tab${isActive ? ' nlv__nav-tab--active' : ''}`}
          >
            Studio
          </NavLink>
          <NavLink
            to="audience"
            className={({ isActive }) => `nlv__nav-tab${isActive ? ' nlv__nav-tab--active' : ''}`}
          >
            Audience
          </NavLink>
          <NavLink
            to="basics"
            className={({ isActive }) => `nlv__nav-tab${isActive ? ' nlv__nav-tab--active' : ''}`}
          >
            Basics
          </NavLink>
        </nav>
      </header>

      <Outlet context={context} />
    </section>
  );
}

/**
 * C1: the workspace, KEYED by the :newsletterId so switching newsletters via the top-bar
 * switcher REMOUNTS the whole subtree. react-router otherwise reuses the same
 * NewsletterWorkspace (and Studio) instance across a :newsletterId change, keeping the
 * previous newsletter's hoisted caches and the Studio's local refs (issueEpoch, the
 * issue/baseline refs, patch timers, pending instruction/treatment maps, selection,
 * dirty flag) alive - so an in-flight generate/patch for newsletter A could settle or
 * PATCH onto newsletter B. The key mints a fresh instance per newsletter, so every cache
 * and ref starts clean and an A-issued write lands only on A's (now-unmounted) setters.
 * This is the route element for the workspace; the child routes (Studio/Audience/Basics)
 * mount inside it as before.
 */
export function KeyedNewsletterWorkspace(): React.JSX.Element {
  const { newsletterId = '' } = useParams();
  return <NewsletterWorkspace key={newsletterId} />;
}
